import { Component, Input, OnChanges, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DiffBlock, alignDiffRows, toDiffBlocks } from './prompt-diff.util';

/**
 * Side-by-side text diff of two prompt versions: old on the left, new on the
 * right, changed lines highlighted per side. Long unchanged runs collapse
 * behind a "N unchanged lines" toggle — prompts run to thousands of lines.
 * Pure presentation: parents fetch the texts (see prompt-history-panel).
 */
@Component({
  selector: 'app-prompt-diff',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './prompt-diff.component.html',
})
export class PromptDiffComponent implements OnChanges {
  @Input({ required: true }) oldText = '';
  @Input({ required: true }) newText = '';
  @Input() oldLabel = 'Old';
  @Input() newLabel = 'New';

  blocks = signal<DiffBlock[]>([]);
  /** Indices of collapsible blocks the user expanded. */
  expanded = signal<Set<number>>(new Set());
  changedRows = signal(0);

  ngOnChanges(): void {
    const rows = alignDiffRows(this.oldText, this.newText);
    this.blocks.set(toDiffBlocks(rows));
    this.expanded.set(new Set());
    this.changedRows.set(rows.filter((r) => r.type !== 'same').length);
  }

  toggle(index: number): void {
    const next = new Set(this.expanded());
    if (!next.delete(index)) {
      next.add(index);
    }
    this.expanded.set(next);
  }

  isExpanded(index: number): boolean {
    return this.expanded().has(index);
  }
}
